/**
 * Dynamically applies span classes to size option items
 * so their content does not overflow and width aligns to
 * a grid layout
 * @module gridSpanSizer
 */

import $ from 'jquery';
import debounce from 'lodash/debounce';

const containerSelector = '.ab839__size-options';
const itemSelector = '.size-options__item';
const maxSpan = 6;
const itemTooltipToggleDisplay = 'size-options__tooltip--toggle';
const spanClassPrefix = 'size-options__item--span';
const debounceDelay = 150;

/**
 * Check if element content is overflowing
 * @private
 * @param {HTMLElement} el - The element to check
 * @returns {boolean} True if overflowing
 */
const isOverflowing = (el) => {
  return el.scrollWidth > el.clientWidth;
};

/**
 * Apply span sizing logic to all items
 * @private
 * @param {jQuery} $container - The container element
 */
const applySpanLogic = ($container) => {
  const items = $container.find(itemSelector);
  
  items.each(function() {
    const $item = $(this);
    $item.addClass(itemTooltipToggleDisplay);
    
    // Remove previous span classes
    const classList = Array.prototype.slice.call(this.classList);
    classList.forEach((cls) => {
      if (cls.indexOf(spanClassPrefix) === 0) {
        $item.removeClass(cls);
      }
    });
    
    let span = 1;
    
    // Try span1 → maxSpan
    while (span <= maxSpan) {
      const cls = spanClassPrefix + span;
      $item.addClass(cls);
      
      if (!isOverflowing(this)) {
        // This span works
        break;
      }
      $item.removeClass(cls);
      span++;
    }
    
    $item.removeClass(itemTooltipToggleDisplay);
  });
};

/**
 * Set up resize event listener
 * @private
 * @param {jQuery} $container - The container element
 */
const setEventListeners = ($container) => {
  const handleResizeStart = () => {
    $container.find(itemSelector).addClass(itemTooltipToggleDisplay);
  };
  const handleResize = debounce(() => {
      applySpanLogic($container);
  }, debounceDelay);

  window.addEventListener('resize', () => {
    handleResizeStart(); // Run immediately
    handleResize();      // Run debounced
  });
};

/**
 * Initializes the grid span sizer plugin
 * @param {HTMLElement} el - Container element
 * @see DEFAULTS
 */
$.gridSpanSizer = () => {
  const $container = $(containerSelector);

  if ($container.length === 0) {
    return;
  }
  
  // Run on page load
  applySpanLogic($container);
  
  // Set up resize listener
  setEventListeners($container);
}


// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/grid-span-sizer.js